import { supabase } from './supabase-client';
import {
  SUPABASE_POSTGREST_TIMEOUT_MS,
  withSupabaseTimeout,
} from './session-guard';
import type { UserProfile } from './types';

/**
 * Load the active user_profiles row for a given auth user ID.
 *
 * Returns the full row (including created_at / updated_at) so the result
 * satisfies the UserProfile type, which mirrors Tables<'user_profiles'> from
 * the generated Supabase types.
 *
 * Filters on id, is_active = true, and can_access_platform = true. Active
 * directory-only recipients can receive assignments and use scoped Magic
 * Links, but must never become full platform users merely because they exist
 * in the assignment roster.
 *
 * Returns null on either:
 *   - no matching row (user not provisioned)
 *   - matching row but is_active = false (user deactivated)
 *   - active directory-only row with can_access_platform = false
 *   - any unexpected error (we fail closed: treat as not provisioned)
 *
 * Errors are not thrown. The caller (AuthProvider.resolveStatus) routes a
 * null return to the 'unprovisioned' AuthStatus state, which is the correct
 * user-facing behavior whether the cause is missing-row or transient error.
 * Surfacing transient errors as auth failures would lock out users on a
 * flaky network; the next page load reattempts.
 */
export async function loadActiveProfile(
  userId: string,
): Promise<UserProfile | null> {
  try {
    const { data, error } = await withSupabaseTimeout(
      supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .eq('is_active', true)
        .eq('can_access_platform', true)
        .maybeSingle(),
      'Active profile lookup',
      SUPABASE_POSTGREST_TIMEOUT_MS,
    );

    if (error) {
      // PGRST116 = no rows found in single() call. Other errors are
      // unexpected (RLS, network, etc.) and we fail closed.
      // maybeSingle() returns data=null without error when no row found,
      // so any error here is a real problem worth logging.
       
      console.warn('loadActiveProfile error', error.message);
      return null;
    }

    return data;
  } catch (e) {
     
    console.warn('loadActiveProfile threw', e);
    return null;
  }
}
